import { useCallback, useContext, useMemo } from 'react';

import { AppContext } from '../../contextStore/AppContext';
import useCcgNavigate from '../../../hooks/useCcgNavigate/useCcgNavigate';
import useEnvironmentContextProvider from '../../contextStore/EnvironmentContext/useEnvironmentContextProvider';
import { getConfigMode } from '../../../utils/session/selectors';
import { isEmbeddedAppEnvironment } from '../../../utils/function/isEmbeddedAppEnvironment';
import isModalType from '../../../utils/container-config/isModalType';
import isDrawerType from '../../../utils/container-config/isDrawerType';
import isPrimarySecondaryContainer from '../../../utils/container-config/isPrimarySecondaryContainer';
import { ELEMENT_IDS } from '../../../utils/element-ids';

type PrimaryActionState = {
  showPrimaryActionButton: boolean;
  primaryActionButtonHandler: (() => void) | undefined;
  primaryActionButtonLabel: string | null;
  primaryActionButtonId: string | undefined;
};

const DONE_BUTTON_LABEL = 'Done';
const RETURN_TO_SELECTION_BUTTON_LABEL = 'Return to selection';
const RETURN_TO_PAYMENT_BUTTON_LABEL = 'Return to payment';
type RouteKeys = 'PAYMENT_WITH_WALLET' | 'PAYMENT_METHOD_SELECT';

/**
 * A single, authoritative hook that provides a unified and STABLE primary action state
 * for the Wallet Management screen.
 */
export const useWalletManagementActions =
  (): PrimaryActionState => {
    const { originalCheckoutSessionResponse } =
      useContext(AppContext);
    const { ccgNavigate } = useCcgNavigate();
    const { appEnvironmentType, containerConfig, onDoneClick } =
      useEnvironmentContextProvider();

    const activeAction = useMemo(() => {
      const configMode = getConfigMode(
        originalCheckoutSessionResponse,
      );
      const isEmbedded = isEmbeddedAppEnvironment(
        appEnvironmentType,
      );

      const isStandaloneModalOrDrawer =
        containerConfig &&
        !isPrimarySecondaryContainer(containerConfig) &&
        (isModalType(containerConfig) ||
          isDrawerType(containerConfig));

      const canShowDoneButton =
        isEmbedded &&
        !!onDoneClick &&
        configMode === 'WALLET' &&
        isStandaloneModalOrDrawer;

      if (canShowDoneButton) {
        return { type: 'done' as const };
      }

      const routeToShow: RouteKeys | null =
        configMode === 'PAYMENT_WITH_WALLET' ||
        configMode === 'PAYMENT_METHOD_SELECT'
          ? configMode
          : null;
      const isHosted = !isEmbedded;
      const shouldShowReturnButton =
        (isHosted || !!containerConfig) && !!routeToShow;

      if (shouldShowReturnButton) {
        return {
          type: 'return' as const,
          route: routeToShow,
          label:
            routeToShow === 'PAYMENT_WITH_WALLET'
              ? RETURN_TO_PAYMENT_BUTTON_LABEL
              : RETURN_TO_SELECTION_BUTTON_LABEL,
        };
      }
      return { type: 'none' as const };
    }, [
      originalCheckoutSessionResponse,
      appEnvironmentType,
      containerConfig,
      onDoneClick,
    ]);

    const primaryActionButtonHandler = useCallback(() => {
      if (activeAction.type === 'done') {
        onDoneClick?.();
      } else if (activeAction.type === 'return') {
        ccgNavigate({ routes: [activeAction.route] });
      }
    }, [activeAction, onDoneClick, ccgNavigate]);

    if (activeAction.type === 'done') {
      return {
        showPrimaryActionButton: true,
        primaryActionButtonHandler,
        primaryActionButtonLabel: DONE_BUTTON_LABEL,
        primaryActionButtonId:
          ELEMENT_IDS.WALLET_MANAGEMENT_DONE_BUTTON,
      };
    }

    if (activeAction.type === 'return') {
      return {
        showPrimaryActionButton: true,
        primaryActionButtonHandler,
        primaryActionButtonLabel: activeAction.label,
        primaryActionButtonId:
          ELEMENT_IDS.WALLET_MANAGEMENT_RETURN_BUTTON,
      };
    }

    // Fallback case: No button
    return {
      showPrimaryActionButton: false,
      primaryActionButtonHandler: undefined,
      primaryActionButtonLabel: null,
      primaryActionButtonId: undefined,
    };
  };
